/**
 * FeedbackDraftImagesModal - Draft 이미지 갤러리
 *
 * draftImages 컬렉션의 캡처된 스크린샷을 이미지 갤러리로 표시합니다.
 * - 개별 이미지 삭제
 * - 이미지 선택 → "피드백 작성" 버튼으로 피드백 폼 이동
 */

import { useEffect, useState, useCallback } from 'react';
import { Trans, t } from '@lingui/macro';
import { Trash2, Loader2, ImageOff, Check, PencilLine, ZoomIn, X } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import type { DraftImage } from '../../models/Feedback';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 선택한 이미지로 피드백 작성 */
  onCreateFeedback: (images: DraftImage[]) => void;
}

export const FeedbackDraftImagesModal: React.FC<Props> = ({
  open, onOpenChange, onCreateFeedback,
}) => {
  const [images, setImages] = useState<DraftImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [zoomImageUrl, setZoomImageUrl] = useState<string | null>(null);

  // Draft 이미지 실시간 구독
  useEffect(() => {
    if (!open || !My.isLoggedIn()) {
      if (!open) setSelectedIds(new Set());
      return;
    }
    setLoading(true);
    const unsub = Fire.feedback.subscribeDraftImages(My.user.id, (imgs) => {
      setImages(imgs);
      setLoading(false);
      // 삭제된 이미지가 선택 상태에 남아 있으면 제거
      setSelectedIds(prev => {
        const validIds = new Set(imgs.map(i => i.id));
        const next = new Set([...prev].filter(id => validIds.has(id)));
        return next.size !== prev.size ? next : prev;
      });
    });
    return () => unsub();
  }, [open]);

  // 이미지 삭제
  const handleDelete = useCallback(async (id: string) => {
    setDeleting(id);
    try {
      await Fire.feedback.deleteDraftImage(My.user.id, id);
    } catch (err) {
      console.error('Failed to delete draft image:', err);
    } finally {
      setDeleting(null);
    }
  }, []);

  // 선택 토글
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // 선택한 이미지로 피드백 작성
  const handleCreateFeedback = () => {
    const selected = images.filter(img => selectedIds.has(img.id));
    if (selected.length === 0) return;
    onCreateFeedback(selected);
  };

  // 시간 포맷
  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);

    if (mins < 1) return t`Just now`;
    if (mins < 60) return t`${mins} min ago`;
    if (hours < 24) return t`${hours} hours ago`;

    return date.toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => { if (!zoomImageUrl) onOpenChange(v); }}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle><Trans>Draft Screenshots</Trans></DialogTitle>
          <DialogDescription>
            <Trans>Select screenshots to attach to your feedback</Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : images.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
              <ImageOff className="w-10 h-10 opacity-40" />
              <p className="text-sm"><Trans>No draft screenshots</Trans></p>
              <p className="text-xs opacity-60"><Trans>Capture a screenshot first using shortcuts or shake</Trans></p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3 py-2">
              {images.map(img => {
                const isSelected = selectedIds.has(img.id);
                const isDeleting = deleting === img.id;
                return (
                  <div
                    key={img.id}
                    className={`relative group rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                      isSelected
                        ? 'border-primary ring-2 ring-primary/20'
                        : 'border-border hover:border-muted-foreground/50'
                    }`}
                    onClick={() => toggleSelect(img.id)}
                  >
                    {/* 이미지 */}
                    <div className="aspect-video bg-muted">
                      <img
                        src={img.imageUrl}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* 선택 체크 */}
                    <div className={`absolute top-2 left-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                      isSelected
                        ? 'bg-primary border-primary'
                        : 'bg-black/30 border-white/70'
                    }`}>
                      {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                    </div>

                    {/* 확대 버튼 */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setZoomImageUrl(img.imageUrl);
                      }}
                      className="absolute top-2 right-10 bg-black/50 hover:bg-black/70 rounded-full p-1.5 transition-opacity opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100"
                    >
                      <ZoomIn className="w-3 h-3 text-white" />
                    </button>

                    {/* 삭제 버튼 */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(img.id);
                      }}
                      disabled={isDeleting}
                      className="absolute top-2 right-2 bg-black/50 hover:bg-destructive rounded-full p-1.5 transition-opacity opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100"
                    >
                      {isDeleting ? (
                        <Loader2 className="w-3 h-3 animate-spin text-white" />
                      ) : (
                        <Trash2 className="w-3 h-3 text-white" />
                      )}
                    </button>

                    {/* 하단 메타 */}
                    <div className="px-2 py-1.5 bg-card/80 backdrop-blur-sm">
                      <p className="text-meta-sm text-muted-foreground truncate">
                        {formatTime(img.capturedAt)}
                      </p>
                      <p className="text-meta-sm text-muted-foreground/60 truncate">
                        {img.platform} · {(() => {
                          try { return new URL(img.currentUrl).pathname; }
                          catch { return img.currentUrl; }
                        })()}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 하단 액션 바 */}
        {images.length > 0 && (
          <div className="pt-4 border-t flex justify-between items-center gap-2 shrink-0">
            <span className="text-xs text-muted-foreground">
              {selectedIds.size > 0
                ? t`${selectedIds.size} selected`
                : t`Tap to select`}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                <Trans>Close</Trans>
              </Button>
              <Button
                onClick={handleCreateFeedback}
                disabled={selectedIds.size === 0}
                className="gap-1.5"
              >
                <PencilLine className="w-4 h-4" />
                <Trans>Write Feedback</Trans>
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* 이미지 확대 보기 모델 */}
    {zoomImageUrl && (
      <Dialog open={true} onOpenChange={() => setZoomImageUrl(null)}>
        <DialogContent className="max-w-[70vw] max-h-[90vh] p-0 border-none bg-transparent shadow-none" hideClose hideTitle>
          <div className="relative w-full h-full flex flex-col items-center justify-center">
            <button
              onClick={() => setZoomImageUrl(null)}
              className="absolute -top-12 right-0 bg-black/50 hover:bg-black/80 rounded-full p-2 transition-colors z-50 text-white"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="overflow-auto rounded-lg max-h-[85vh] bg-black/20 flex flex-col items-center justify-center p-2">
              <img src={zoomImageUrl} alt="Preview" className="max-w-full max-h-full object-contain" />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
};
